import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@project-mgmt/feature-auth";
import { useWorkspaces } from "@project-mgmt/feature-workspaces";
import {
  AppFooter,
  AppShell,
  HierarchicalBreadcrumbs,
  MobileTabBar,
  Stack,
  WorkspaceSwitcher,
  type NavigationItem,
} from "@project-mgmt/ui";
import { routes } from "../utils/routes";
import { appLinks } from "../config/links";
import { OnlineStatus } from "./OnlineStatus";
import { PageErrorBoundary } from "./ErrorBoundary";
import { OfflineIndicator } from "./OfflineIndicator";
import { useBreadcrumbHierarchy } from "../hooks/useBreadcrumbHierarchy";

interface LayoutRouteParams {
  workspaceId?: string;
  projectId?: string;
  taskId?: string;
  ventureId?: string;
}

function LayoutContent() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams() as LayoutRouteParams;
  const { data: workspacesData, isLoading: isWorkspacesLoading, error: workspacesError } = useWorkspaces();
  const { levels } = useBreadcrumbHierarchy();

  const workspaces =
    workspacesData?.workspaces.map((workspace) => ({
      id: workspace.id,
      name: workspace.name,
      role: workspace.role,
    })) ?? [];

  if (location.pathname === routes.login() || !isAuthenticated) {
    return (
      <PageErrorBoundary>
        <OfflineIndicator />
        <Outlet />
      </PageErrorBoundary>
    );
  }

  const navigationItems: NavigationItem[] = [
    {
      id: "workspaces",
      label: "Workspaces",
      onClick: () => navigate(routes.workspaces(), { state: { skipAutoRestore: true } }),
      active: location.pathname === routes.workspaces() || /^\/workspaces\/[^/]+$/.test(location.pathname),
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      id: "projects",
      label: "Projects",
      onClick: () => workspaceId && navigate(routes.projects(workspaceId)),
      active: Boolean(workspaceId) && location.pathname.includes("/projects"),
      disabled: !workspaceId,
      disabledTooltip: "Select a workspace first",
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      ),
    },
    {
      id: "ventures",
      label: "Ventures",
      onClick: () => workspaceId && navigate(routes.ventures(workspaceId)),
      active: Boolean(workspaceId) && location.pathname.includes("/ventures"),
      disabled: !workspaceId,
      disabledTooltip: "Select a workspace first",
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
  ];

  const handleWorkspaceSelect = (selectedWorkspaceId: string) => {
    if (!selectedWorkspaceId || selectedWorkspaceId === workspaceId) {
      return;
    }

    navigate(routes.workspace(selectedWorkspaceId));
  };

  const handleBreadcrumbNavigate = (path: string) => {
    navigate({
      pathname: path,
      search: location.search,
    });
  };

  const handleLogout = () => {
    logout();
    navigate(routes.login());
  };

  const logo = (
    <div className="group flex items-center gap-2">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[color:rgb(0_191_255_/_0.25)] bg-[color:var(--color-primary)] text-sm font-black text-white shadow-[var(--shadow-premium)]">
        VB
      </span>
      <span className="text-xl font-black tracking-tighter text-textPrimary">
        Venture <span className="text-[color:var(--color-primary)]">Builders</span>
      </span>
    </div>
  );

  const workspaceSwitcher = (
    <WorkspaceSwitcher
      currentWorkspaceId={workspaceId}
      workspaces={workspaces}
      isLoading={isWorkspacesLoading}
      error={workspacesError}
      onWorkspaceSelect={handleWorkspaceSelect}
      className="w-full max-w-[320px]"
    />
  );

  const userMenu = (
    <Stack direction="row" spacing="sm" align="center">
      <span className="text-textSecondary hidden text-sm sm:block">{user?.name || user?.phoneNumber}</span>
      <button
        onClick={handleLogout}
        className="text-textSecondary hover:text-textPrimary touch-target rounded-md px-3 py-2 text-sm transition-colors"
      >
        Logout
      </button>
    </Stack>
  );

  const mobileTabItems = [navigationItems[0], navigationItems[1], navigationItems[2]].map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon!,
    onClick: item.onClick!,
    active: item.active,
    badge: item.badge,
  }));

  return (
    <PageErrorBoundary>
      <OfflineIndicator />
      <div className="full-height flex flex-col">
        <AppShell
          navigation={navigationItems}
          logo={logo}
          workspaceSwitcher={workspaceSwitcher}
          userMenu={userMenu}
          className="flex-1"
        >
          <div className="flex min-h-full flex-col">
            <div className="flex-1 pb-16 md:pb-0">
              {isAuthenticated && levels.length > 0 && (
                <div className="border-border bg-surface mx-auto max-w-6xl border-b px-4 py-2 sm:px-6 lg:px-8">
                  <HierarchicalBreadcrumbs levels={levels} onNavigate={handleBreadcrumbNavigate} />
                </div>
              )}

              <PageErrorBoundary>
                <Outlet />
              </PageErrorBoundary>
            </div>

            <AppFooter
              className="mt-auto"
              links={[
                { id: "privacy", label: "Privacy", href: appLinks.privacy },
                { id: "terms", label: "Terms", href: appLinks.terms },
                { id: "support", label: "Support", href: appLinks.support },
              ]}
              statusInfo={<OnlineStatus />}
              showBranding={true}
            />
          </div>
        </AppShell>

        <MobileTabBar items={mobileTabItems} />
      </div>
    </PageErrorBoundary>
  );
}

export function Layout() {
  return <LayoutContent />;
}
